import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'scripts',
  slug: 'scripts',
  name: 'Scripts',
  description: 'The script library and gated on-demand execution against your fleet.',
  icon: 'Terminal',
  endpoints: [
    {
      slug: 'list',
      method: 'GET',
      path: '/api/scripts',
      summary: 'List scripts',
      description: 'Returns every script registered in this tenant\'s library.',
      auth: 'session',
      responseExample: `[{ "id": "scr_1a2", "name": "Clear temp files", "language": "powershell", "category": "Maintenance" }]`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'create',
      method: 'POST',
      path: '/api/scripts',
      summary: 'Register a script',
      description: 'Adds a script to the library. This registers content only — running it is a separate, explicitly gated action.',
      auth: 'session',
      requestBodyExample: `{ "name": "Clear temp files", "language": "powershell", "category": "Maintenance", "body": "Remove-Item $env:TEMP\\\\* -Recurse -Force" }`,
      responseExample: `{ "id": "scr_1a3", "name": "Clear temp files" }`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'run',
      method: 'POST',
      path: '/api/scripts/:id/run',
      summary: 'Run a script on-demand',
      description: 'Refused (403) until an admin explicitly enables on-demand run for the tenant (Settings -> Scripts). Requires confirm: true and an explicit target every time — there is no implicit "run against everything."',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Script id.' }],
      requestBodyExample: `{ "confirm": true, "target": "dev_10ac" }`,
      responseExample: `{ "ok": true, "jobId": "job_88f1", "status": "queued" }`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'get',
      method: 'GET',
      path: '/api/scripts/:id',
      summary: 'Get a script',
      description: 'Returns one script\'s full record, including its body. 404 if the id doesn\'t exist in this tenant\'s library.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Script id.' }],
      responseExample: `{ "id": "scr_1a2", "name": "Clear temp files", "description": "", "language": "powershell", "category": "Maintenance", "tags": "", "body": "Remove-Item $env:TEMP\\\\* -Recurse -Force", "action1Id": "", "lastPushedAt": "", "lastRunAt": "", "runCount": 0, "createdAt": "2026-06-01T12:00:00.000Z", "updatedAt": "2026-06-01T12:00:00.000Z", "createdBy": "jklein" }`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'update',
      method: 'PUT',
      path: '/api/scripts/:id',
      summary: 'Update a script',
      description: 'Replaces name, description, language, category, tags, and body on an existing script. Fields go through the same cleaning as create (name required, language falls back to "powershell" if not one of the four supported values). Does not touch action1Id, lastPushedAt, lastRunAt, or runCount.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Script id.' }],
      requestBodyExample: `{ "name": "Clear temp files", "language": "powershell", "category": "Maintenance", "body": "Remove-Item $env:TEMP\\\\* -Recurse -Force -ErrorAction SilentlyContinue" }`,
      responseExample: `{ "id": "scr_1a2", "name": "Clear temp files", "description": "", "language": "powershell", "category": "Maintenance", "tags": "", "body": "Remove-Item $env:TEMP\\\\* -Recurse -Force -ErrorAction SilentlyContinue", "action1Id": "", "lastPushedAt": "", "lastRunAt": "", "runCount": 0, "createdAt": "2026-06-01T12:00:00.000Z", "updatedAt": "2026-07-20T09:00:00.000Z", "createdBy": "jklein" }`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'delete',
      method: 'DELETE',
      path: '/api/scripts/:id',
      summary: 'Delete a script',
      description: 'Removes a script from the library. This does not revoke or delete anything already pushed to Action1 as a saved script — that copy stays on the Action1 side. Unlike get/update, this does not 404 for a missing id; it always returns ok.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Script id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/scripts.js',
    },
    {
      slug: 'push',
      method: 'POST',
      path: '/api/scripts/:id/push',
      summary: 'Push a script to Action1',
      description: 'Pushes the script\'s current body to Action1 as a saved script through the tenant\'s configured Action1 RMM integration, and records the returned action1Id plus lastPushedAt on the script. Returns 400 if Action1 is not configured for the tenant (Admin > Action1 RMM) or 502 if Action1 rejects the push. This only stages the script in Action1 — it does not run it (see run-on-demand for that, gated separately).',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Script id.' }],
      responseExample: `{ "ok": true, "action1Id": "a1-scr-9f21" }`,
      sourceFile: 'routes/scripts.js, lib/script-run.js',
    },
    {
      slug: 'rmm-mgmt-register',
      method: 'POST',
      path: '/api/rmm-mgmt/scripts',
      summary: 'Register a signed fleet-automation script',
      description: 'Registers (or re-registers) one id+version of a script in the RMM signed-script registry (ADR-0052, Phase C automations) — a separate system from the library above, used for the signed fleet-job execution path rather than ad hoc on-demand runs. The server signs {id, version, sha256, language} with the tenant\'s active signer key and stores the record; an enrolled, device-key-authenticated agent later fetches it by id+version (POST /api/ext/rmm-mgmt/scripts/fetch) and re-verifies the signature and content hash before executing. Returns 409 if the signer hasn\'t been initialized yet (POST /api/rmm-mgmt/signer/init first) or 422 if id/version/content fail validation. id must match `^[a-z0-9][a-z0-9._-]{0,79}$`, version must match `^[a-z0-9][a-z0-9._-]{0,39}$`. Gated by the `admin` grant (RMM super-admin), because a registered record can be dispatched as a signed fleet job.',
      auth: 'session',
      requestBodyExample: `{ "id": "clear-temp", "version": "1.0.1", "content": "Remove-Item $env:TEMP\\\\* -Recurse -Force -ErrorAction SilentlyContinue" }`,
      responseExample: `{
  "ok": true,
  "script": {
    "id": "clear-temp",
    "version": "1.0.1",
    "sha256": "9f2a1c7e0b...b7",
    "language": "powershell",
    "signerKid": "kid_2026_07",
    "signedAt": "2026-07-20T09:00:00.000Z",
    "registeredBy": "jklein",
    "bytes": 58
  }
}`,
      sourceFile: 'routes/rmm-mgmt-scripts.js, lib/rmm-scripts.js',
    },
  ],
};
